import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../utils/queryClient';
import { toast } from '../../components/ui/Toast/useToast';
import { QUERY_KEYS } from '../../config/constants';
import size from 'lodash/size';
import isArray from 'lodash/isArray';
import { 
    getCompanyGroupsFn, 
    getCompanyGroupFieldsFn,
    getCompanyGroupByIdFn,
    impersonateFleetAdminFn,
    getCompanyGroupEditableFieldsFn,
    addCompanyGroupFn,
    assignMembersToGroupFn,
    unassignMembersToGroupFn,
    createShadowUserFn,
    updateGroupByIdFn,
    deleteGroupByIdFn } from '../../transactions/user/groupAdmin';

const {
  COMPANY_GROUPS,
  COMPANY_GROUPS_FIELDS,
  COMPANY_GROUPS_EDITABLE_FIELDS,
  COMPANY_GROUPS_DETAILS,
} = QUERY_KEYS;

export const useGetCompanyGroupsList = ({ length, search, page }) => {
  return useInfiniteQuery({
    queryKey: [
      COMPANY_GROUPS,
      {
        length,
        search,
        page,
      },
    ],
    queryFn: getCompanyGroupsFn,
    staleTime: Infinity,
    cacheTime: Infinity,
    getNextPageParam: (lastPage, pages) => {
      return pages.length + 1;
    },
    onSuccess: (data) => {
      // Handle success if needed
    //   console.log('Company Groups:', data);
    },
    
  });
}

export const useGetCompanyGroupsFields = () => {
    return useQuery({
        queryKey: [COMPANY_GROUPS_FIELDS],
        queryFn: getCompanyGroupFieldsFn,
        staleTime: Infinity,
        cacheTime: Infinity,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
        // Handle success if needed
        },
    });
}

export const useGetCompanyGroupDetails = (groupId) => {
  return useQuery({
      queryKey: [COMPANY_GROUPS_DETAILS, groupId],
      queryFn: () => getCompanyGroupByIdFn(groupId),
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
      // Handle success if needed
      },
  });
}

export const useGetCompanyGroupsEditableFields = (groupId) => {
  return useQuery({
      queryKey: [COMPANY_GROUPS_EDITABLE_FIELDS],
      queryFn: () => getCompanyGroupEditableFieldsFn(groupId),
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
      // Handle success if needed
      // console.log('Company Groups:', data);
      },
  });
}

export const useAddGroup = () => {
  return useMutation({
      mutationFn: (payload) => addCompanyGroupFn(payload),
  });
};

export const useImpersonateFleetAdmin = () => {
  return useMutation({
      mutationFn: (impersonateUserid) => impersonateFleetAdminFn(impersonateUserid),
      onSuccess: (response) => {
        // console.log(response)
        if (response && response.exceptions) {
          if (size(response.exceptions) > 0) {
            const exceptionsArray = isArray(response.exceptions);
            const message = exceptionsArray
              ? response.exceptions.slice(-1)
              : 'Παρουσιάστηκε κάποιο πρόβλημα';
            toast({
              variant: 'destructive',
              description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
            });
          }
        }
        if (response && response.data?.access_token) {
          const jwt = response?.data?.access_token;
          const fleetAdminJWT = localStorage.getItem('jwt');
  
          if (jwt && fleetAdminJWT) {
            localStorage.setItem('isLoggedIn', false);
            localStorage.setItem('fleetAdminJWT', fleetAdminJWT);
            localStorage.setItem('jwt', jwt);
            localStorage.removeItem('user-info')
            window.location.replace(`/impersonate?jwt=${jwt}`);
          }
        }
      },
      onError: () => {
        toast({
          variant: 'destructive',
          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
        });
      }
  });
};

export const useAssignMemberToGroup = () => {
  return useMutation({
      mutationFn: (requestData) => assignMembersToGroupFn(requestData.groupid, requestData.payload),
  });
};

export const useUnassignMemberToGroup = () => {
  return useMutation({
      mutationFn: (requestData) => unassignMembersToGroupFn(requestData.groupid, requestData.payload),
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: [COMPANY_GROUPS],
        });
      },
      onError: () => {
        toast({
          variant: 'destructive',
          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
        });
      }
  });
};

export const useCreateShadowUser = () => {
  return useMutation({
      mutationFn: (payload) => createShadowUserFn(payload),
      onSuccess: (response) => {
        if (response && response.exceptions) {
          if (size(response.exceptions) > 0) {
            const exceptionsArray = isArray(response.exceptions.invalid.afm) || isArray(response.invalid.afm);
            const message = exceptionsArray
              ? response.exceptions.invalid.afm.slice(-1)
              : 'Παρουσιάστηκε κάποιο πρόβλημα';
            // console.log(exceptionsArray)
            toast({
              variant: 'destructive',
              description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
            });
          }
        }
      },
      onError: () => {
        toast({
          variant: 'destructive',
          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
        });
      }
  });
};

export const useUpdateGroup = () => {
  return useMutation({
    mutationFn: ({ groupId, payload }) => updateGroupByIdFn(groupId, payload),
  });
};

export const useDeleteGroup = () => {
  return useMutation({
      mutationFn: (groupId) => deleteGroupByIdFn(groupId),
  });
};